import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAuthenticatedAccount } from '@core/Components/RequireAuth/hooks'
import { SettingItemWithCheckbox } from '../Common/SettingItemWithCheckbox'
import {
  getPushNotificationAlertStatus,
  togglePushNotificationAlertStatus,
} from '@core/Utils/notifications/pushNotificationAlerts'

// DEV testing, use this status response:
// const pushAuthStatus = {
//   fcmRegistrationToken: 'FAKE_TOKEN_1',
//   status: 'Authorized',
// }

export const PayoutNotification: React.FC = () => {
  const { accountNumber } = useAuthenticatedAccount()
  const [isEnabled, setIsEnabled] = React.useState<boolean | null>(null)

  React.useEffect(() => {
    getPushNotificationAlertStatus(accountNumber, 'payout')
      .then(isEnabled => {
        if (typeof isEnabled !== 'boolean') return
        setIsEnabled(isEnabled)
      })
      .catch()
  }, [accountNumber])

  const { mutate, isPending } = useMutation<void, void, OnToggleParams>({
    mutationFn: ({ accountNumber, isEnabled }) =>
      onToggleCreditAlerts({ accountNumber, isEnabled }),
    onSuccess: () => setIsEnabled(curr => !curr),
  })

  if (isEnabled === null) return null

  return (
    <SettingItemWithCheckbox
      isEnabled={isEnabled}
      label='Payout Notification'
      desciption='Enables an alert when you get a payout'
      onToggle={() => mutate({ accountNumber, isEnabled: !isEnabled })}
      shouldDisable={isPending}
      isLoading={isPending}
    />
  )
}

// =============
// Local Helpers
// =============

async function onToggleCreditAlerts({
  accountNumber,
  isEnabled,
}: {
  accountNumber: number
  isEnabled: boolean
}) {
  await togglePushNotificationAlertStatus({
    accountNumber,
    isEnabled,
    deepLink: `${window.location.origin}/account/activity/results`,
  })
}

// =====
// Types
// =====

type OnToggleParams = Parameters<typeof onToggleCreditAlerts>[0]
